
'use client';

import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';

export default function VideoSection() {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const videoSteps = [
    {
      time: '0-3s',
      label: 'HOOK',
      text: '"I used to go through 3 lighters a month until I found this..."',
      icon: '🎯',
    },
    {
      time: '3-7s',
      label: 'THE PROBLEM',
      text: '"Cheap lighters always die at the worst moments. Rain, cold, or just poor quality."',
      icon: '💔',
    },
    {
      time: '7-20s',
      label: 'DEMONSTRATION',
      text: '"This rechargeable pistol lighter works every time. USB charging, windproof flame, plus it stores your cigarettes safely. Watch this..."',
      icon: '⚡',
    },
    {
      time: '20-30s',
      label: 'PROOF',
      text: '"I\'ve had mine for 8 months. Still works perfectly. No fuel, no batteries, no problems."',
      icon: '✅',
    },
    {
      time: '30-40s',
      label: 'CALL TO ACTION',
      text: '"Link in bio. Get yours before they sell out again."',
      icon: '🔥',
    },
  ];

  return (
    <section
      id="video"
      ref={ref}
      className="py-12 sm:py-16 md:py-24 lg:py-32 bg-black relative overflow-hidden"
    >
      {/* Background Gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-black via-zinc-900/50 to-black pointer-events-none" />

      <div className="max-w-6xl mx-auto px-4 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-8 sm:mb-12 md:mb-16"
        >
          <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-white mb-3 sm:mb-4 md:mb-6">
            See It In Action
          </h2>
          <p className="text-white/70 text-sm sm:text-base md:text-lg max-w-2xl mx-auto">
            Watch how this tactical lighter changes everything about your daily carry
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12 items-start">
          {/* Video Player - Facebook Embed */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            <div className="relative aspect-[9/16] sm:aspect-[9/16] bg-zinc-900 rounded-lg overflow-hidden shadow-2xl">
              <iframe
                src="https://www.facebook.com/plugins/video.php?height=476&href=https%3A%2F%2Fwww.facebook.com%2Fmugart.pk%2Fvideos%2F9876468025702223%2F&show_text=false&width=267&t=0"
                className="w-full h-full border-0"
                style={{ border: 'none', overflow: 'hidden' }}
                scrolling="no"
                frameBorder="0"
                allowFullScreen={true}
                allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
              />
            </div>

            {/* Video Stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 mt-4 sm:mt-6"
            >
              <div className="flex items-center gap-2 text-white/70 text-xs sm:text-sm">
                <i className="ri-eye-line text-base sm:text-lg" />
                <span>127K views</span>
              </div>
              <div className="flex items-center gap-2 text-white/70 text-xs sm:text-sm">
                <i className="ri-thumb-up-line text-base sm:text-lg" />
                <span>8.2K likes</span>
              </div>
              <div className="flex items-center gap-2 text-white/70 text-xs sm:text-sm">
                <i className="ri-share-line text-base sm:text-lg" />
                <span>2.1K shares</span>
              </div>
            </motion.div>
          </motion.div>

          {/* Video Script Timeline */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="space-y-3 sm:space-y-4"
          >
            <h3 className="text-white text-lg sm:text-xl md:text-2xl font-medium mb-4 sm:mb-6">
              Video Breakdown
            </h3>

            {videoSteps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: 20 }}
                animate={isInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.4 + index * 0.1 }}
                className="relative pl-10 sm:pl-12 pb-4 sm:pb-6 border-l-2 border-white/20 last:border-transparent last:pb-0"
              >
                {/* Icon */}
                <div className="absolute left-0 top-0 -translate-x-1/2 w-8 h-8 sm:w-10 sm:h-10 bg-zinc-900 border-2 border-white/30 rounded-full flex items-center justify-center text-base sm:text-lg">
                  {step.icon}
                </div>

                {/* Content */}
                <div>
                  <div className="flex items-center gap-2 mb-1 sm:mb-2">
                    <span className="text-white/50 text-[10px] sm:text-xs font-mono">
                      {step.time}
                    </span>
                    <span className="text-white/70 text-[10px] sm:text-xs font-medium uppercase tracking-wider">
                      {step.label}
                    </span>
                  </div>
                  <p className="text-white/90 text-xs sm:text-sm md:text-base leading-relaxed italic">
                    {step.text}
                  </p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="text-center mt-8 sm:mt-12 md:mt-16"
        >
          <p className="text-white/60 text-xs sm:text-sm md:text-base mb-4 sm:mb-6">
            Over 12,000 satisfied customers in the last 30 days
          </p>
          <button
            onClick={() => {
              const offerSection = document.getElementById('offer');
              if (offerSection) {
                offerSection.scrollIntoView({ behavior: 'smooth' });
              }
            }}
            className="bg-white text-black px-6 py-3 sm:px-8 sm:py-4 rounded-full text-sm sm:text-base md:text-lg font-medium hover:bg-white/90 transition-all whitespace-nowrap cursor-pointer active:scale-95 shadow-xl"
          >
            Get Yours Now — $49.90
          </button>
        </motion.div>
      </div>
    </section>
  );
}
